import { date } from 'quasar'
const { formatDate, addToDate } = date
import { isConsumer, isKiosk, isPos } from 'src/config'
import Generic from 'src/models/generic/Generic'
import Model, { encoders, executeMutation, GqlError, ID, Integer, IsoDateTime, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Movie from 'src/models/movie/Movie'
import MovieTerm from 'src/models/movie/MovieTerm'
import Order from 'src/models/order/Order'
import BookingWeek, { validateDayNumber } from 'src/models/schedule/BookingWeek'
import BookingWeekMovie from 'src/models/schedule/BookingWeekMovie'
import { defaultMovieMinutes } from 'src/models/schedule/ShowingSlot'
import TimeSlot, { timeToMinutes } from 'src/models/schedule/TimeSlot'
import Screen from 'src/models/screen/Screen'
import SeatChart from 'src/models/screen/SeatChart'
import PriceCard from 'src/models/showing/PriceCard'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import TicketType from 'src/models/showing/TicketType'
import Ticket from 'src/models/ticket/Ticket'
import { prependWithConsumerRoot } from 'src/router/utils'
import seatSaleRestrictionService from 'src/services/seats/SeatSaleRestrictionService'

const isStation = isPos || isKiosk

export const DUE_TIME_MINUTES_FROM_SHOWING_START = -10

export const SHOWING_CHANGE_ACTIONS = {
  CANCEL: 'cancel',
  RESCHEDULE: 'reschedule',
  SCREEN_SWAP: 'screen_swap'
}
SHOWING_CHANGE_ACTIONS.ALL = Object.values(SHOWING_CHANGE_ACTIONS)

export default class Showing extends Model {
  static name = 'Showing'

  static versioned = true

  static validatable = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmProgramming]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      time: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      movie: {
        type: Movie,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      movieTerm: {
        type: MovieTerm,
        reference: true,
        default: null,
        output: true
      },
      bookingWeekMovie: {
        type: BookingWeekMovie,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      bookingWeekMovieName: {
        type: String,
        default: null,
        local: true
      },
      showingId: {
        type: ID,
        default: null,
        output: true
      },
      showing: {
        type: Showing,
        default: null,
        output: true,
        reference: true
      },
      showings: {
        type: [Showing],
        default: () => ([]),
        acceptsNestedAttributes: true,
        allowDestroy: true
      },
      isMarathon: {
        type: Boolean,
        default: false,
        computed () {
          if (this.isRemote) return this.data.isMarathon

          return !!this.movie?.isMarathon
        },
        output: true
      },
      hasMarathon: {
        type: Boolean,
        default: false,
        computed () {
          if (this.isRemote) return this.data.hasMarathon

          return !!this.showingId
        },
        output: true
      },
      allowSalesInMarathon: {
        type: Boolean,
        default: false
      },
      screen: {
        type: Screen,
        reference: true,
        encode: encoders.hasOne
      },
      originalScreen: {
        type: Screen,
        reference: true,
        encode: encoders.hasOne,
        output: true
      },
      overrideSeatChart: {
        type: Boolean,
        default: false
      },
      customSeatChart: {
        type: SeatChart,
        encode: encoders.hasOne
      },
      hasSeatChart: {
        type: Boolean,
        default: false,
        output: true
      },
      seatChart: {
        type: SeatChart,
        output: true
      },
      priceCard: {
        type: PriceCard,
        reference: true,
        output: true
      },
      overridePriceCard: {
        type: Boolean,
        default: false
      },
      customPriceCard: {
        type: PriceCard,
        reference: true,
        encode: encoders.hasOne
      },
      overridePostStartTimeBufferMinutes: {
        type: Boolean,
        default: false
      },
      customPostStartTimeBufferMinutes: {
        type: Integer,
        default: 10
      },
      published: {
        type: Boolean,
        default: false
      },
      ticketsSold: {
        type: Integer,
        default: 0,
        output: true
      },
      marathonTicketsSold: {
        type: Integer,
        default: 0,
        output: true
      },
      ticketsPaid: {
        type: Integer,
        default: 0,
        output: true
      },
      seatsRemaining: {
        type: Integer,
        default: 0,
        output: true,
        excludeFromDefault: true
      },
      seatsRemainingByTicketTypeId: {
        type: JsonObject,
        default: {},
        output: true,
        excludeFromDefault: true
      },
      seatsRemainingWithoutSocialDistancing: {
        type: Integer,
        default: 0,
        output: true,
        excludeFromDefault: true
      },
      predictedAttendance: {
        type: Integer,
        excludeFromDefault: true,
        default: null,
        output: true
      },
      current: {
        type: Boolean,
        output: true
      },
      past: {
        type: Boolean,
        output: true
      },
      overrideReservedSeating: {
        type: Boolean,
        default: false
      },
      overrideReservedSeatingValue: {
        type: Boolean,
        default: false
      },
      customHeldSeatCount: {
        type: Integer,
        default: 0
      },
      overrideHeldSeatCount: {
        type: Boolean,
        default: false
      },
      customMarathonSeatCount: {
        type: Integer,
        default: 0
      },
      overrideMarathonSeatCount: {
        type: Boolean,
        default: false
      },
      releasedSections: {
        type: JsonArray,
        excludeFromDefault: true
      },
      showingBadges: {
        type: [ShowingBadge],
        reference: true,
        output: true
      },
      additionalShowingBadges: {
        type: [ShowingBadge],
        reference: true
      },
      overrideShowingBadges: {
        type: Boolean,
        default: false
      },
      allowWithoutMembership: {
        type: Boolean,
        default: false
      },
      disableTheaterSeatDelivery: {
        type: Boolean,
        default: false
      },
      qrItemOrderingOnly: {
        type: Boolean,
        default: false
      },
      allowConsumerRefunds: {
        type: Boolean,
        default: true
      },
      allowConsumerQrTabWithoutPaymentMethod: {
        type: Boolean,
        default: false
      },
      allowItemOrdersOnline: {
        type: Boolean,
        output: true,
        computed () {
          return this.data.allowItemOrdersOnline && !this.movie?.disableOnlineConcessions && !this.data.qrItemOrderingOnly
        }
      },
      customShowingBadges: {
        type: [ShowingBadge],
        reference: true
      },
      bookingWeek: {
        type: BookingWeek,
        reference: true,
        local: true
      },
      orders: {
        type: [Order],
        reference: true,
        output: true
      },
      tickets: {
        type: [Ticket],
        reference: true,
        output: true
      },
      private: {
        type: Boolean,
        default: false
      },
      isPreview: {
        type: Boolean,
        default: false
      },
      displayMetaData: {
        type: JsonObject,
        output: true
      },
      action: {
        type: String,
        options: SHOWING_CHANGE_ACTIONS.ALL,
        default: null,
        input: true
      },
      showingChangeConfig: {
        type: JsonObject,
        default: null,
        input: true
      },
      ticketTypeClones: {
        type: [TicketType],
        default: () => ([]),
        local: true
      },
      metrics: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      overrideMaxTicketsPerOrderPerShowing: {
        type: Boolean,
        default: false
      },
      maxTicketsPerOrderPerShowing: {
        type: Integer,
        default: 10
      }
    }

    this.computed = {
      siteTime () {
        if (!this.time) return this.time

        return this.time.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      movieSiteTime () {
        if (!this.movie?.siteId) return this.siteTime
        if (this.movie.siteId === this.configuration.currentSite?.id) return this.siteTime

        const movieSiteTimeZone = this.movie.site?.timeZone
        if (!movieSiteTimeZone) return this.siteTime

        return this.time.inTimeZone(movieSiteTimeZone)
      },
      dayNumber () {
        if (!this.time) return

        return new IsoDateTime(addToDate(this.time, { hours: -4 })).inTimeZone(this.configuration.currentSite?.timeZone).getDay()
      },
      postStartTimeBufferMinutes () {
        if (this.overridePostStartTimeBufferMinutes) {
          return this.customPostStartTimeBufferMinutes
        } else if (this.showing?.overridePostStartTimeBufferMinutes) {
          return this.showing?.customPostStartTimeBufferMinutes
        } else if (this.movie?.overridePostStartTimeBufferMinutes) {
          return this.movie?.customPostStartTimeBufferMinutes
        } else {
          return this.screen.postStartTimeBufferMinutes
        }
      },
      ticketLimit () {
        if (this.overrideMaxTicketsPerOrderPerShowing) {
          return this.maxTicketsPerOrderPerShowing
        } else if (this.priceCard?.overrideMaxTicketsPerOrderPerShowing) {
          return this.priceCard.maxTicketsPerOrderPerShowing
        } else if (this.movie?.overrideMaxTicketsPerOrderPerShowing) {
          return this.movie.maxTicketsPerOrderPerShowing
        } else if (this.screen?.overrideMaxTicketsPerOrderPerShowing) {
          return this.screen.maxTicketsPerOrderPerShowing
        } else if (this.configuration.currentSite?.hasMaxTicketsPerOrderPerShowing) {
          return this.configuration.currentSite?.maxTicketsPerOrderPerShowing
        }
        return null
      },
      endTime () {
        return new IsoDateTime(addToDate(this.time, { minutes: this.totalDurationMinutes }))
      },
      timeMs () {
        return this.time.getTime()
      },
      endTimeMs () {
        return this.time.getTime() + this.totalDurationMinutes * 60 * 1000
      },
      siteEndTime () {
        if (!this.endTime) return this.endTime

        return this.endTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      timeSlot () {
        if (!this.siteTime) return

        const timeSlot = TimeSlot.timeSlotForDateTime(this.siteTime)
        if (!timeSlot) {
          throw new Error('No matching time slot for ' + this.siteTime + ' ' + this.id)
        }
        return timeSlot
      },
      isNearCapacity () {
        const twentyPercentRemaining = 0.2
        return (this.seatsRemaining / this.seatCount) <= twentyPercentRemaining
      },
      seatCount () {
        if (this.screen?.reservedSeating && this.screen?.seatChart) {
          return this.screen?.seatChart?.seatCount
        } else {
          return this.screen?.seatCount
        }
      },
      heldSeatCount () {
        return this.overrideHeldSeatCount ? this.customHeldSeatCount : this.screen.heldSeatCount
      },
      marathonSeatCount () {
        if (this.overrideMarathonSeatCount) {
          return this.customMarathonSeatCount || 0
        } else if (this.showing?.overrideMarathonSeatCount) {
          return this.showing?.customMarathonSeatCount || 0
        } else {
          return this.screen?.marathonSeatCount || 0
        }
      },
      isSoldOut () {
        return this.seatsRemaining <= 0
      },
      suggestedDeliveryTimes () {
        const timeNow = new Date()

        const deliveryTimes = []

        if (this.time > timeNow) {
          deliveryTimes.push({
            label: 'Start',
            time: this.siteTime,
            minutes: Math.round((this.time - timeNow) / 1000 / 60)
          })
        }

        const earlyTime = new IsoDateTime(addToDate(this.time, { minutes: this.totalDurationMinutes * 0.2 }))
        if (earlyTime > timeNow) {
          deliveryTimes.push({
            label: 'Early',
            time: earlyTime.inTimeZone(this.configuration.currentSite?.timeZone),
            minutes: Math.round((earlyTime - timeNow) / 1000 / 60)
          })
        }

        const middleTime = new IsoDateTime(addToDate(this.time, { minutes: this.totalDurationMinutes * 0.4 }))
        if (middleTime > timeNow) {
          deliveryTimes.push({
            label: 'Middle',
            time: middleTime.inTimeZone(this.configuration.currentSite?.timeZone),
            minutes: Math.round((middleTime - timeNow) / 1000 / 60)
          })
        }

        const lateTime = new IsoDateTime(addToDate(this.time, { minutes: this.totalDurationMinutes * 0.7 }))
        if (lateTime > timeNow) {
          deliveryTimes.push({
            label: 'Late',
            time: lateTime.inTimeZone(this.configuration.currentSite?.timeZone),
            minutes: Math.round((lateTime - timeNow) / 1000 / 60)
          })
        }

        return deliveryTimes
      },
      isPastDeliveryCutoffTime () {
        const currentTime = this.configuration.currentSite.siteTime

        if ((this.siteEndTime - currentTime) < 0) return false

        const timeToEndInMilliseconds = this.siteEndTime - currentTime
        const timeToEndInMinutes = Math.floor(timeToEndInMilliseconds / (1000 * 60))

        return timeToEndInMinutes < this.configuration.currentSite.theaterSeatDeliveryCutoffMinutes
      },
      isSeatDeliveryDisabled () {
        return this.isPastDeliveryCutoffTime || this.disableTheaterSeatDelivery
      },
      isFuture () {
        const currentTime = new Date().getTime()
        const ninetyMinutes = 90 * 60 * 1000
        return this.timeMs > (currentTime + ninetyMinutes)
      },
      isPast () {
        const currentTime = new Date().getTime()
        return currentTime > this.endTimeMs
      }
    }

    this.watchers = [
      [
        function () { return this.published },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.published = this.published
          })
        }
      ],
      [
        function () { return this.private },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.private = this.private
          })
        }
      ],
      [
        function () { return this.isPreview },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.isPreview = this.isPreview
          })
        }
      ],
      [
        function () { return this.screen },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.screen = this.screen
          })
        }
      ],
      [
        function () { return [this.overrideReservedSeating, this.overrideReservedSeatingValue, this.overrideHeldSeatCount, this.customHeldSeatCount] },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.overrideReservedSeating = this.overrideReservedSeating
            marathonShowing.overrideReservedSeatingValue = this.overrideReservedSeatingValue
            marathonShowing.overrideHeldSeatCount = this.overrideHeldSeatCount
            marathonShowing.customHeldSeatCount = this.customHeldSeatCount
          })
        }
      ],
      [
        function () { return [this.overrideSeatChart, this.overrideReservedSeatingValue] },
        async function () {
          if (!this.isMarathon) return

          _.each(this.showings.data, marathonShowing => {
            marathonShowing.overrideSeatChart = this.overrideSeatChart
            marathonShowing.customSeatChart = this.customSeatChart
          })
        }
      ],
      [
        function () { return [this.overridePostStartTimeBufferMinutes, this.customPostStartTimeBufferMinutes] },
        async function () {
          if (this.isMarathon) {
            this.updateMarathonShowingsTimes()
          } else if (this.hasMarathon) {
            this.showing.updateMarathonShowingsTimes()
          }
        }
      ]
    ]

    this.fragments = {
      select: ['id', 'time']
    }

    this.queries = {
      showingsForDate: {
        arguments: {
          date: {
            type: String,
            required: false
          },
          siteId: {
            type: ID,
            required: false
          },
          ids: {
            type: [ID],
            required: false
          },
          movieId: {
            type: ID,
            required: false
          },
          movieIds: {
            type: [ID],
            required: false
          },
          titleClassId: {
            type: ID,
            required: false
          },
          titleClassIds: {
            type: [ID],
            required: false
          },
          siteIds: {
            type: [ID],
            required: false
          },
          everyShowingBadgeIds: {
            type: [ID],
            required: false
          },
          anyShowingBadgeIds: {
            type: [ID],
            required: false
          },
          resultVersion: {
            type: String,
            required: false
          }
        },
        results: {
          showings: [Showing]
        }
      },

      publicShowingsForMovie: {
        arguments: {
          movieId: {
            type: ID,
            required: true
          }
        },
        results: {
          showings: [Showing]
        }
      },

      currentShowingsByScreenSummary: {
        results: {
          summary: Generic
        }
      },

      currentShowingByDeliveryLocationName: {
        arguments: {
          deliveryLocationName: {
            type: String,
            required: true
          }
        },
        results: {
          showing: Showing
        }
      },

      datesWithShowing: {
        arguments: {
          siteId: {
            type: ID,
            required: false
          },
          ids: {
            type: [ID],
            required: false
          },
          movieId: {
            type: ID,
            required: false
          },
          movieIds: {
            type: [ID],
            required: false
          },
          titleClassId: {
            type: ID,
            required: false
          },
          titleClassIds: {
            type: [ID],
            required: false
          },
          siteIds: {
            type: [ID],
            required: false
          },
          everyShowingBadgeIds: {
            type: [ID],
            required: false
          },
          anyShowingBadgeIds: {
            type: [ID],
            required: false
          },
          resultVersion: {
            type: String,
            required: false
          }
        },
        results: {
          dates: Generic
        }
      }
    }

    this.mutations = {
      recomputeShowingData: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          showing: Showing,
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
  }

  get checkoutUrl () {
    return this.movie?.site ? this.movie.site.url(this.checkoutPath) : this.checkoutPath
  }

  get checkoutPath () {
    if (this.movie?.urlSlugOrId && !this.reservedSeating) {
      const path = `${this.pathRoot}/${this.movie.urlSlugOrId}/${this.id}`
      return isConsumer ? prependWithConsumerRoot(path) : path
    }
    return this.checkoutPathWithoutMovie
  }

  get checkoutPathWithoutMovie () {
    const path = `${this.pathRoot}/${this.id}`
    return isConsumer ? prependWithConsumerRoot(path) : path
  }

  get pathRoot () {
    return this.reservedSeating && !this.configuration.currentSite.consumerConfig.showTicketFirstCheckoutFlow ? '/checkout/seats' : '/checkout/showing'
  }

  async executeShowingChange (config = null) {
    this.showingChangeConfig = config

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('showingChangeConfig')
      this.resetField('action')
    }
  }

  addMinutes (minutes) {
    this.time = new IsoDateTime(addToDate(this.time, { minutes }))
    this.updateMarathonShowingsTimes()
  }

  setBookingWeekDate (startOfWeek) {
    while (this.time > startOfWeek) {
      this.time = date.addToDate(this.time, { days: -7 })
    }
    while (this.time < startOfWeek) {
      this.time = date.addToDate(this.time, { days: 7 })
    }
    this.updateMarathonShowingsTimes()
  }

  setDayNumber (dayNumber, bookingWeekStartDayNumber) {
    const daysFromBookingWeekStart = validateDayNumber(dayNumber - bookingWeekStartDayNumber)
    const currentTime = new IsoDateTime(this.time).inTimeZone(this.configuration.currentSite.timeZone)
    const currentDaysFromBookingWeekStart = validateDayNumber(currentTime.getDay() - bookingWeekStartDayNumber)
    this.time = date.addToDate(this.time, { days: daysFromBookingWeekStart - currentDaysFromBookingWeekStart })

    this.updateMarathonShowingsTimes()
  }

  // A showing with marathon showings updates their start times as needed
  // Marathon showing times cannot be updated directly except for buffers
  updateMarathonShowingsTimes () {
    if (this.isMarathon) {
      let priorEndTime = this.time
      _.each(this.showings.data, marathonShowing => {
        marathonShowing.time = priorEndTime
        priorEndTime = marathonShowing.endTime
      })
    }
  }

  formattedTime (showDate = false, relative = false, isoDateTime = null, minutesToAdd = 0) {
    const today = formatDate(new Date(), 'YYYY-MM-DD')
    const tomorrow = formatDate(addToDate(new Date(), { days: 1 }), 'YYYY-MM-DD')

    const showingDate = formatDate(isoDateTime || this.siteTime, 'YYYY-MM-DD')

    let time = isoDateTime || this.siteTime
    time = addToDate(time, { minutes: minutesToAdd })

    const timeFormatted = formatDate(time, 'h:mm a')

    if (!showDate) {
      return timeFormatted
    }

    let dateFormatted = ''

    if (relative && showingDate === today) {
      dateFormatted = this.i18n.t('time.today')
    } else if (relative && showingDate === tomorrow) {
      dateFormatted = this.i18n.t('time.tomorrow')
    } else {
      dateFormatted = formatDate(time, 'MMM Do')
    }

    return `${dateFormatted} at ${timeFormatted}`
  }

  packages (mode = 'all', withSortValue = false, seatObject = null) {
    const packages = _(this.priceCard?.priceCardItems?.data)
      .filter(({ itemPackage }) => {
        if (!itemPackage) return false

        // Hide tickets if not set to online
        if (!isPos && !itemPackage.availableOnline) return false

        // Membership specific tickets must be associated with a user, skip
        const packageTicketTypes = _.compact(_.map(itemPackage.itemPackageItems.data, packItem => packItem.ticketType))

        const packageContainsMembership = _.some(packageTicketTypes, packageTicket => packageTicket.membershipType)
        if (mode === 'guest' && packageContainsMembership) return false

        if (_.some(packageTicketTypes, ticketType => _.includes(ticketType.excludeRatings, this.movie.rating))) return false

        // Stop here if we don't have a seat chart to evaluate
        // We want to proceed even if reserve seating is off
        // We do this so that specific seat type tickets can still be sold
        if (!this.seatChart) return true

        if (_.some(packageTicketTypes, 'appliesToSpecificSeats')) {
          // Only include if there is a seat in the chart that matches this type
          return _.difference(_.map(packageTicketTypes, 'id'), this.seatTicketTypes).length === 0
        } else {
          // If the ticket type doesn't apply to a specific seat, return true only if the auditorium has general seating
          return this.hasGeneralSeating
        }
      })
      .sortBy(({ displayOrder }) => displayOrder)
      .map(({ displayOrder, itemPackage }) => {
        if (withSortValue) {
          return [displayOrder, itemPackage]
        } else {
          return itemPackage
        }
      })
      .value()

    return packages
  }

  ticketTypes (mode = 'all', withSortValue = false, seatObject = null) {
    return _(this.priceCard?.priceCardItems?.data)
      .filter(({ ticketType }) => {
        if (_.isNil(ticketType)) return false

        if (!_.isNil(seatObject) && !seatSaleRestrictionService.isSeatSaleAllowed(seatObject, ticketType)) return false

        // Hide tickets if not set to online
        if (!isPos && !ticketType.availableOnline) return false

        if (isStation && !ticketType.availableAtStations) return false

        // Membership specific tickets must be associated with a user, skip
        if (mode === 'guest' && ticketType.membershipType) return false

        if (_.includes(ticketType.excludeRatings, this.movie.rating)) return false

        // Stop here if we don't have a seat chart to evaluate
        // We want to proceed even if reserve seating is off
        // We do this so that specific seat type tickets can still be sold
        if (_.isNil(this.seatChart)) return true

        if (ticketType.appliesToSpecificSeats) {
          // Only include if there is a seat in the chart that matches this type
          return _.includes(this.seatTicketTypes, ticketType.id)
        } else {
          // If the ticket type doesn't apply to a specific seat, return true only if the auditorium has general seating
          return this.hasGeneralSeating
        }
      })
      .sortBy(({ displayOrder }) => displayOrder)
      .map((priceCardItem) => {
        let foundTicketType = _.find(this.ticketTypeClones.data, ticketClone => ticketClone.id === priceCardItem.ticketType.id)
        if (!foundTicketType) {
          const clone = priceCardItem.ticketType.clone()
          this.ticketTypeClones.add(clone)
          foundTicketType = clone
        }
        if (priceCardItem.hasCustomPrice) {
          foundTicketType.price = priceCardItem.customPrice
        }
        if (withSortValue) {
          return [priceCardItem.displayOrder, foundTicketType]
        } else {
          return foundTicketType
        }
      })
      .value()
  }

  memberPackages (member, withSortValue = false) {
    // Look for a match for membership type and user class if set
    const packages = _(this.packages('all', true)).map(([sortOrder, pack]) => {
      const packageTicketTypes = _.compact(_.map(pack.itemPackageItems.data, packItem => packItem.ticketType))
      const isMemberPackage = _.every(packageTicketTypes, ticketType =>
        (!ticketType.membershipType || ticketType.membershipType?.id === member.effectiveMembershipType?.id) &&
        (!ticketType.userClass || ticketType.userClass?.id === member.userClass?.id) &&
        (!_.includes(ticketType.excludeRatings, this.movie.rating))
      )
      if (withSortValue && isMemberPackage) {
        return [sortOrder, pack]
      } else if (isMemberPackage) {
        return pack
      }
    }).compact().value()

    return packages
  }

  memberTicketTypes (member, withSortValue = false) {
    // Look for a match for membership type and user class if set
    const ticketTypes = _(this.ticketTypes('all', true)).map(([sortOrder, ticketType]) => {
      const isMemberTicket = (!ticketType.membershipType || ticketType.membershipType?.id === member.effectiveMembershipType?.id) &&
        (!ticketType.userClass || ticketType.userClass?.id === member.userClass?.id) &&
        (!_.includes(ticketType.excludeRatings, this.movie.rating))
      if (withSortValue && isMemberTicket) {
        return [sortOrder, ticketType]
      } else if (isMemberTicket) {
        return ticketType
      }
    }
    ).compact().value()

    return ticketTypes
  }

  showingStartingSoon (withinMinutes = 30) {
    return !this.showingStarted && this.time < addToDate(new Date(), { minutes: withinMinutes })
  }

  async recomputeData () {
    const results = await executeMutation(
      'recomputeShowingData',
      {
        id: this.id
      },
      {
        showing: {
          queryName: 'showing',
          key: 'showing',
          structure: {
            id: true,
            priceCard: {
              id: true
            },
            movieTerm: {
              id: true,
              showingBadges: {
                id: true
              }
            },
            showingBadges: {
              id: true
            }
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    if (_.isEmpty(results.errors)) this.setData(results.data.showing, true)

    return results
  }

  get planElement () {
    return this.bookingWeekMovie?.planElement
  }

  get description () {
    if (this.movie) {
      return `${this.movie.name} at ${this.formattedRelativeDateTime}`
    } else {
      return this.formattedRelativeDateTime
    }
  }

  get formattedRelativeDateTime () {
    return this.formattedTime(true, true)
  }

  get descriptionWithDefaultDueTime () {
    if (this.movie) {
      return `${this.movie.name}, ${this.formattedTime(true, true, null, DUE_TIME_MINUTES_FROM_SHOWING_START)}`
    } else {
      return this.formattedTime(true, true, null, DUE_TIME_MINUTES_FROM_SHOWING_START)
    }
  }

  get localPast () {
    return this.time < addToDate(new Date(), { hours: -2 })
  }

  get beforeCutoffTime () {
    return this.time < addToDate(new Date(), { minutes: -this.configuration.currentSite.showPastShowingsOnlineCutoffMinutes })
  }

  get hasPurchaseEmbargo () {
    return this.movie.embargoPurchaseLiftedAt && this.embargoPurchaseLiftedAtInSiteTime - this.configuration.currentSite.siteTime > 0
  }

  get embargoPurchaseLiftedAt () {
    return this.movie.embargoPurchaseLiftedAt
  }

  get embargoPurchaseLiftedAtInSiteTime () {
    return IsoDateTime.parse(this.embargoPurchaseLiftedAt).inTimeZone(this.configuration.currentSite.timeZone)
  }

  get validOnlinePurchaseTime () {
    if (this.movie?.allowPastSales && !this.showingFinished) return true
    if (this.hasPurchaseEmbargo) return false

    return this.time > addToDate(new Date(), { minutes: this.configuration.currentSite.showingCutoffOnlineMinutes })
  }

  get totalDurationMinutes () {
    if (this.isMarathon && this.showings?.data?.length) {
      return _.sumBy(this.showings.data, 'totalDurationMinutes')
    }

    return (this.movie?.duration || defaultMovieMinutes) + this.postStartTimeBufferMinutes
  }

  get ticketLimit () {
    return this.ticketLimit
  }

  get formattedRelativeFinishedDateTime () {
    return this.formattedTime(true, true, this.siteEndTime)
  }

  get showingStarted () {
    const currentTime = new IsoDateTime()
    return this.time < currentTime
  }

  get showingFinished () {
    const finishedTime = new IsoDateTime(addToDate(this.time, { minutes: this.totalDurationMinutes }))
    const currentTime = new IsoDateTime()
    return finishedTime < currentTime
  }

  get timeFormatted () {
    return formatDate(this.time, 'HH:mm')
  }

  get minutesOfDate () {
    return timeToMinutes(this.time)
  }

  get hasGeneralSeating () {
    return _(this.seatChart?.seatChart)
      .flatten()
      .some(seat => seat.available && _.isNil(seat.areaCategoryId))
  }

  get reservedSeating () {
    if (!this.configuration.currentSite?.hasReservedSeating) return false

    if (this.overrideReservedSeating) return this.overrideReservedSeatingValue

    return this.screen.reservedSeating
  }

  get seatTicketTypes () {
    const areaCategoryIds = _(this.seatChart?.seatChart)
      .flatten()
      .filter('available')
      .map('areaCategoryId')
      .flatten().compact().uniq().value()

    return _(this.configuration.seatChartAreaCategories.data).filter(areaCategory => _.includes(areaCategoryIds, areaCategory.id)).map('effectiveTicketTypeIds').flatten().compact().uniq().value()
  }

  get computedPriceCard () {
    return (this.overridePriceCard && this.customPriceCard) ||
        (this.movie?.overridePriceCard && this.movie?.customPriceCard) ||
        (this.planElement?.overridePriceCard && this.planElement.customPriceCard) ||
        (this.screen?.overridePriceCard && this.screen.customPriceCard) ||
        this.configuration.currentSite?.defaultPriceCard(this.dayNumber, this.timeSlot?.id)
  }

  get priceCardSource () {
    return (this.overridePriceCard && this.customPriceCard && 'Showing') ||
        (this.movie.overridePriceCard && this.movie.customPriceCard && 'Movie') ||
        (this.planElement?.overridePriceCard && this.planElement.customPriceCard && 'Plan Element') ||
        (this.screen?.overridePriceCard && this.screen.customPriceCard && 'Screen') ||
        (this.configuration.currentSite?.defaultPriceCard(this.dayNumber, this.timeSlot?.id) && 'Time Slot')
  }

  get priceCardTicketTypes () {
    return this.ticketTypes()
  }

  get computedShowingBadges () {
    if (this.overrideShowingBadges) {
      return this.customShowingBadges.data
    } else {
      return _([
        this.movie?.showingBadges?.data,
        this.screen?.showingBadges?.data,
        this.planElement?.showingBadges?.data,
        this.additionalShowingBadges?.data,
        this.priceCard?.showingBadges?.data,
        [this.reservedSeating ? this.configuration.currentCircuit.reservedSeatingShowingBadge : null]
      ]).flatten().compact().uniqBy('id').sortBy('displayOrder').value()
    }
  }

  get availableMarathonShowings () {
    return _.filter(this.showings.data, 'allowSalesInMarathon')
  }
}
